// Shared types for HornDeliver services.
// These mirror the Prisma models but are safe to import on the client.

// Roles: customer (default sender), provider (merchant/business), driver (courier), merchant, admin.
// Users pick provider | driver during registration; admin is granted separately.
// "customer" is kept as the sender-mode for backwards compat (providers can also send).
// V1.0: "merchant" role owns a Store and uses the merchant dashboard.
export type Role = "customer" | "provider" | "driver" | "merchant" | "admin";

// V1.0: dual-mode — pickup_deliver (errand for an existing external order) vs
// order_deliver (order placed in-platform from a Store/merchant).
export type JobMode = "pickup_deliver" | "order_deliver";

export type JobStatus =
  | "open"
  | "negotiating"
  | "assigned"
  | "picked_up"
  | "in_transit"
  | "delivered"
  | "completed"
  | "cancelled"
  | "disputed";

export type OfferStatus = "pending" | "accepted" | "rejected" | "countered" | "expired";

export type TrustStatus = "none" | "pending" | "verified" | "rejected";

export type PaymentStatus = "pending" | "collected" | "confirmed" | "failed";

export type DisputeStatus = "open" | "investigating" | "resolved" | "closed";

export type VehicleType = "any" | "motorcycle" | "car" | "badgag" | "pickup" | "truck" | "bicycle";

// Phase 2: delivery verticals.
// Phase 3: added courier + errand.
// Phase 4: added water (drinking water delivery).
export type JobVertical = "parcel" | "food" | "grocery" | "pharmacy" | "courier" | "errand" | "water" | "other";

export interface User {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  countryCode: string; // Phase 2: "+251" | "+252" | "+253"
  activeRole: Role;
  isAdmin: boolean;
  language: string;
  avatarUrl: string | null;
  rating: number;
  ratingCount: number;
  emailVerified: boolean; // Phase 2
  phoneVerified: boolean; // Phase 2
  fraudScore: number; // Phase 2
  flagged: boolean; // Phase 2
  // Multi-role: progressive verification per HornDeliver-Multiple-Roles.md
  senderVerified: boolean; // customer role — instant (email+phone)
  providerVerified: boolean; // merchant role — admin approval required
  courierVerified: boolean; // driver role — admin approval required
  // V1.0 Monetization: provider commission tier ladder.
  commissionTier: string; // launch_partner | standard | premium | enterprise
  tierExpiresAt: string | null; // when Launch Partner status auto-reverts
  tierRenewalSentAt: string | null; // last renewal-reminder email timestamp
  createdAt: string;
  trustStatus: TrustStatus;
}

// Multi-role: which roles a user is verified for.
export type RoleVerificationStatus = "pending" | "approved" | "rejected";
export type ApplicationRole = "provider" | "courier";

// Role application — a user's submission to become a verified Provider or Courier.
export interface RoleApplication {
  id: string;
  userId: string;
  role: ApplicationRole;
  status: RoleVerificationStatus;
  // Provider documents
  businessName: string | null;
  taxId: string | null;
  storePhotoUrl: string | null;
  bankingDetails: string | null;
  // Courier documents
  licenseNumber: string | null;
  licensePhotoUrl: string | null;
  vehicleInsuranceUrl: string | null;
  vehiclePhotoUrl: string | null;
  backgroundCheckUrl: string | null;
  vehicleType: string | null;
  // Review
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
  createdAt: string;
  // Joined user (for admin list)
  user?: { id: string; name: string; email: string; phone: string | null };
}

// Wallet types — financial separation per role.
export type WalletType = "sender" | "courier" | "provider";

export interface Wallet {
  id: string;
  userId: string;
  type: WalletType;
  balance: number;
  currency: string;
  pendingBalance: number;
  createdAt: string;
  updatedAt: string;
}

export type WalletTransactionType = "credit" | "debit" | "transfer_in" | "transfer_out";

export interface WalletTransaction {
  id: string;
  walletId: string;
  type: WalletTransactionType;
  amount: number;
  balanceAfter: number;
  description: string | null;
  relatedWalletId: string | null;
  jobId: string | null;
  createdAt: string;
}

// Helper: check if a user is verified for a given role.
export function isRoleVerified(user: User, role: Role): boolean {
  if (role === "admin") return user.isAdmin;
  if (role === "customer") return user.senderVerified;
  if (role === "provider" || role === "merchant") return user.providerVerified;
  if (role === "driver") return user.courierVerified;
  return false;
}

export interface DeliveryJob {
  id: string;
  code: string;
  customerId: string;
  providerId: string | null;
  status: JobStatus;
  mode: JobMode; // V1.0: pickup_deliver | order_deliver
  storeId: string | null; // V1.0: for order_deliver mode
  parcelCategory: JobVertical;
  parcelDescription: string;
  parcelWeightKg: number | null;
  parcelSize: string | null;
  fragile: boolean;
  metadata: string | null; // Phase 2: JSON string for food/grocery
  receiptPhotoUrl: string | null; // V1.0: proof of existing external order
  scheduledFor: string | null; // V1.0: scheduled pickup (null = immediate)
  quotedDistanceKm: number | null; // V1.0: instant-quote distance
  quotedDeliveryFee: number | null; // V1.0: courier delivery fee
  quotedServiceFee: number | null; // V1.0: consumer platform service fee
  pickupAddress: string;
  pickupLat: number | null;
  pickupLng: number | null;
  pickupNotes: string | null;
  dropoffAddress: string;
  dropoffLat: number | null;
  dropoffLng: number | null;
  dropoffNotes: string | null;
  vehicleRequired: VehicleType;
  contactPhone: string | null;
  customerOffer: number;
  agreedPrice: number | null;
  commissionAmount: number | null; // Phase 2
  providerPayout: number | null; // Phase 2
  confirmationCode: string | null;
  proofPhotoUrl: string | null; // V1.0: proof-of-delivery photo
  assignedAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  preparingAt: string | null; // V1.0 Order & Deliver tracking
  outForDeliveryAt: string | null; // V1.0 Order & Deliver tracking
  createdAt: string;
  updatedAt: string;
  customer?: User;
  provider?: User | null;
  store?: Store | null;
  offers?: Offer[];
  payment?: Payment | null;
}

// Phase 2: typed metadata shapes per vertical.
export interface FoodMetadata {
  restaurantName?: string;
  orderItems?: { name: string; qty: number; price: number }[];
  orderTotal?: number;
  specialInstructions?: string;
  readyBy?: string;
}

export interface GroceryMetadata {
  storeName?: string;
  items?: { name: string; qty: number; estimatedPrice: number }[];
  estimatedTotal?: number;
  substitutionsAllowed?: boolean;
  shoppingWindow?: string;
}

// Phase 3: Pharmacy metadata.
export interface PharmacyMetadata {
  pharmacyName?: string;
  prescriptionRequired?: boolean;
  prescriptionNumber?: string;
  medicineItems?: { name: string; qty: number }[];
  temperatureControlled?: boolean;
}

// Phase 3: Courier metadata.
export interface CourierMetadata {
  documentType?: "contract" | "legal" | "bank" | "other";
  signatureRequired?: boolean;
  urgency?: "normal" | "urgent" | "same_day";
}

// Phase 3: Errand metadata.
export interface ErrandMetadata {
  errandType?: "bill" | "pickup" | "queue" | "other";
  errandLocation?: string;
  errandNotes?: string;
}

export interface Offer {
  id: string;
  jobId: string;
  providerId: string;
  amount: number;
  message: string | null;
  status: OfferStatus;
  counterAmount: number | null;
  etaMinutes: number | null;
  createdAt: string;
  respondedAt: string | null;
  provider?: User;
}

export interface ChatMessage {
  id: string;
  jobId: string;
  senderId: string;
  receiverId: string;
  content: string;
  type: "text" | "offer" | "system" | "image";
  offerId: string | null;
  read: boolean;
  createdAt: string;
  sender?: User;
}

export interface Payment {
  id: string;
  jobId: string;
  amount: number;
  method: string;
  status: PaymentStatus;
  confirmationCode: string | null;
  collectedById: string | null;
  confirmedById: string | null;
  collectedAt: string | null;
  confirmedAt: string | null;
  note: string | null;
  commissionAmount: number | null; // Phase 2
  commissionRuleId: string | null; // Phase 2
  providerPayout: number | null; // Phase 2
  createdAt: string;
}

export interface Dispute {
  id: string;
  jobId: string;
  raisedById: string;
  reason: string;
  description: string;
  status: DisputeStatus;
  resolution: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  createdAt: string;
  job?: DeliveryJob;
  raisedBy?: User;
}

export interface TrustRecord {
  id: string;
  userId: string;
  status: TrustStatus;
  idType: string | null;
  idNumber: string | null;
  idFrontUrl: string | null;
  idBackUrl: string | null;
  selfieUrl: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
}

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  data: string | null;
  channel: string;
  read: boolean;
  delivered: boolean;
  emailSent: boolean; // Phase 2
  emailSentAt: string | null; // Phase 2
  pushAttempted: boolean; // Phase 2
  createdAt: string;
}

// Phase 2: Advertising.
export interface Advertisement {
  id: string;
  advertiserId: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  targetUrl: string | null;
  ctaLabel: string;
  vertical: string;
  countryCode: string | null;
  status: "draft" | "active" | "paused" | "expired";
  startsAt: string;
  endsAt: string | null;
  impressions: number;
  clicks: number;
  pricingModel: "cpm" | "cpc" | "flat";
  budget: number | null;
  spent: number;
  // V1.0 Monetization: "Boost My Store" — link ad to a specific storefront.
  storeId: string | null;
  dailyBudget: number | null; // per-day spend cap ($10-$100)
  createdAt: string;
  updatedAt: string;
  advertiser?: User;
}

// Phase 2: Commission rules.
export interface CommissionRule {
  id: string;
  name: string;
  vertical: string;
  type: "percentage" | "fixed";
  rate: number;
  minAmount: number;
  maxAmount: number | null;
  active: boolean;
  // V1.0 Monetization: tier-aware rules + dual-mode support.
  tier: string | null; // launch_partner | standard | premium | enterprise | null
  mode: string; // order_deliver | pickup_deliver | all
  minMonthlyVolume: number | null; // Launch Partner MOQ (null = no minimum)
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Phase 2: Fraud flags.
export interface FraudFlag {
  id: string;
  userId: string;
  jobId: string | null;
  raisedById: string | null;
  rule: string;
  severity: "low" | "medium" | "high";
  status: "open" | "reviewing" | "resolved" | "dismissed";
  description: string;
  metadata: string | null;
  resolvedById: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  createdAt: string;
  user?: User;
  job?: DeliveryJob | null;
}

// Domain event types emitted across bounded contexts.
export type DomainEventType =
  | "user.registered"
  | "job.created"
  | "offer.made"
  | "offer.counter"
  | "offer.accepted"
  | "offer.rejected"
  | "job.assigned"
  | "job.picked_up"
  | "job.in_transit"
  | "job.delivered"
  | "job.completed"
  | "job.cancelled"
  | "message.sent"
  | "payment.collected"
  | "payment.confirmed"
  | "trust.submitted"
  | "trust.verified"
  | "trust.rejected"
  | "dispute.opened"
  | "dispute.resolved"
  // Phase 2:
  | "commission.applied"
  | "fraud.flagged"
  | "ad.impression"
  | "ad.click"
  | "phone.verified"
  | "email.verified"
  // Multi-role: progressive verification lifecycle.
  | "role.application.submitted"
  | "role.application.approved"
  | "role.application.rejected";

export interface DomainEvent {
  type: DomainEventType;
  jobId?: string;
  actorId?: string;
  payload: Record<string, unknown>;
}

// State machine transitions for delivery jobs.
// NOTE: The P2P "negotiating" status is dormant — the current business model is
// Pickup & Deliver + Order & Deliver with direct courier assignment (no offer
// bargaining). `open` now transitions directly to `assigned` via the
// /api/services/job/[id]/accept endpoint. The `negotiating` state is retained
// in the union + transitions only so any legacy rows can still resolve cleanly.
export const JOB_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  open: ["assigned", "cancelled"],
  negotiating: ["assigned", "cancelled"],
  assigned: ["picked_up", "cancelled", "disputed"],
  picked_up: ["in_transit", "disputed"],
  in_transit: ["delivered", "disputed"],
  delivered: ["completed", "disputed"],
  completed: [],
  cancelled: [],
  disputed: ["investigating" as JobStatus, "completed", "cancelled"],
};

export function canTransition(from: JobStatus, to: JobStatus): boolean {
  const allowed = JOB_TRANSITIONS[from] || [];
  return allowed.includes(to);
}

// Phase 2: parse a job's metadata JSON safely.
export function parseJobMetadata<T = FoodMetadata | GroceryMetadata>(
  raw: string | null
): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

// ============================================================
// Phase 3 types: subscriptions, mobile money, sync.
// ============================================================

export interface SubscriptionPlan {
  id: string;
  code: string; // free | merchant | enterprise
  name: string;
  description: string | null;
  priceMonthly: number;
  priceAnnual: number;
  features: string | null; // JSON array
  commissionDiscount: number;
  // V1.0 Monetization: absolute commission rate this plan unlocks (e.g. 7 = 7%).
  commissionRate: number | null;
  // V1.0 Monetization: who this plan is for (provider = Pro/Enterprise).
  targetRole: string; // provider | customer
  // Maps to User.commissionTier when subscribed.
  tierCode: string | null; // launch_partner | standard | premium | enterprise
  jobLimit: number | null;
  seatLimit: number | null;
  active: boolean;
  sortOrder: number;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: "active" | "cancelled" | "expired" | "past_due";
  billingCycle: "monthly" | "annual";
  amountPaid: number;
  startsAt: string;
  endsAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  plan?: SubscriptionPlan;
}

export type MobileMoneyProvider = "dmoney" | "ebirr" | "zaad" | "waafi";

export interface MobileMoneyAccount {
  id: string;
  userId: string;
  provider: MobileMoneyProvider;
  accountNumber: string;
  accountName: string | null;
  verified: boolean;
  active: boolean;
  createdAt: string;
}

export interface MobileMoneyTransaction {
  id: string;
  accountId: string;
  paymentId: string | null;
  jobId: string | null;
  provider: MobileMoneyProvider;
  amount: number;
  currency: string;
  type: "payment" | "refund" | "payout";
  status: "pending" | "success" | "failed";
  reference: string;
  note: string | null;
  createdAt: string;
}

// ============================================================
// Phase 4: Service Status — admin toggle to enable/disable a
// delivery vertical when the underlying service is interrupted
// (e.g. water shortage, pharmacy closure, courier strike).
// Disabled verticals are hidden from the customer dashboard
// quick-post tiles and reject new job creation.
// ============================================================

export interface ServiceStatus {
  id: string;
  vertical: string; // parcel | food | grocery | pharmacy | courier | errand | water
  enabled: boolean;
  reason: string | null; // admin note, e.g. "Water supply interrupted — resumes 2026-02-01"
  updatedById: string | null;
  updatedAt: string;
  createdAt: string;
}

// All delivery verticals the platform supports, with display metadata.
export const VERTICALS = [
  "parcel",
  "food",
  "grocery",
  "pharmacy",
  "courier",
  "errand",
  "water",
] as const;
export type VerticalKey = (typeof VERTICALS)[number];

// ============================================================
// V1.0 — Store / Merchant Marketplace (Order & Deliver mode)
// ============================================================

export type StoreCategory =
  | "restaurant"
  | "grocery"
  | "pharmacy"
  | "convenience"
  | "other";

export type StoreStatus = "pending" | "approved" | "suspended" | "rejected";

// V1.0 monetization: merchant commission tiers (15-30%).
export type CommissionTier = "basic" | "premium" | "flagship";

export interface Store {
  id: string;
  ownerId: string;
  name: string;
  description: string | null;
  category: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  imageUrl: string | null;
  bannerUrl: string | null;
  rating: number;
  ratingCount: number;
  commissionTier: string;
  commissionRate: number;
  status: StoreStatus;
  rejectionReason: string | null;
  active: boolean;
  countryCode: string;
  featured: boolean;
  // V1.0 Monetization: "Boost My Store" sponsored storefront.
  boostedUntil: string | null; // when the boost expires (null = not boosted)
  boostDailyBudget: number | null; // merchant-set daily budget ($10-$100)
  boostSpentToday: number; // accumulated spend today
  boostStartedAt: string | null; // when the current boost campaign started
  boostLastResetAt: string | null; // last time boostSpentToday was reset
  createdAt: string;
  updatedAt: string;
  owner?: User;
  products?: Product[];
  _distanceKm?: number; // optional, computed when browsing by location
  _sponsored?: boolean; // V1.0 Monetization: true if boostedUntil > now
}

export interface Product {
  id: string;
  storeId: string;
  name: string;
  description: string | null;
  price: number;
  imageUrl: string | null;
  category: string | null;
  available: boolean;
  discountedPrice: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  store?: Store;
}

// V1.0: instant-quote request + response (Pickup & Deliver fee calculator).
export interface QuoteRequest {
  pickupLat?: number;
  pickupLng?: number;
  dropoffLat?: number;
  dropoffLng?: number;
  weightKg?: number;
  size?: "small" | "medium" | "large";
  scheduledFor?: string | null; // null = immediate (on-demand)
  mode?: JobMode;
}

export interface QuoteBreakdown {
  baseFee: number;
  distanceFee: number;
  weightFee: number;
  sizeFee: number;
  instantPremium: number; // surcharge for immediate dispatch
  peakSurge: number; // dynamic pricing during high-demand periods
  serviceFee: number; // consumer platform service fee (3-5%)
  total: number; // total the customer pays
  courierPayout: number; // what the courier earns
  distanceKm: number;
  isPeak: boolean;
  isImmediate: boolean;
}

// V1.0: cart line item for Order & Deliver checkout.
export interface CartItem {
  product: Product;
  qty: number;
}

// V1.0: courier earnings summary.
export interface CourierEarnings {
  today: number;
  week: number;
  completedTrips: number;
  bonuses: number;
  pendingPayout: number;
  trips: DeliveryJob[];
}

// ============================================================
// V1.0 Monetization: Boost / Upgrade / Revenue Mix types
// ============================================================

// "Boost My Store" sponsored storefront status payload.
export interface StoreBoost {
  active: boolean;
  boostedUntil: string | null;
  boostDailyBudget: number | null;
  boostSpentToday: number;
  boostStartedAt: string | null;
  daysRemaining: number;
}

// Provider "Upgrade to Pro" savings estimate (Module 1).
export interface UpgradeCalc {
  currentTier: string;
  currentRate: number;
  monthlyCommissionPaid: number;
  proPlanRate: number;
  proPlanPrice: number;
  proSavings: number;
  enterprisePlanRate: number;
  enterprisePlanPrice: number;
  enterpriseSavings: number;
}

// Admin "Launch Partner alerts" row — providers on the 5% launch rate whose
// tier is about to expire, with their 30-day MOQ status.
export interface LaunchPartnerAlert {
  userId: string;
  storeName: string;
  ownerName: string;
  tierExpiresAt: string;
  daysUntilExpiry: number;
  monthlyOrderCount: number;
  belowMoq: boolean;
}

// Admin "Revenue Mix" dashboard (Module 3) — commission + subscriptions +
// advertising breakdown with MoM growth.
export interface RevenueMix {
  commission: number;
  subscriptions: number;
  advertising: number;
  total: number;
  commissionPct: number;
  subscriptionsPct: number;
  advertisingPct: number;
  momGrowthPct: number;
  previousMonthTotal: number;
}
